import { components } from "@/api-schema";

type UserWithMembership = components["schemas"]["UserWithMembershipResponseDto"];

export interface GroupMembershipSelectionState {
  discoveredInitialUsers: UserWithMembership[];
  addedUsers: UserWithMembership[];
  removedInitialUserUuids: string[];
  isInitialAutoSelectionSuspended: boolean;
  lastSeededPageSignature: string | undefined;
}

export function createGroupMembershipSelectionState(): GroupMembershipSelectionState {
  return {
    discoveredInitialUsers: [],
    addedUsers: [],
    removedInitialUserUuids: [],
    isInitialAutoSelectionSuspended: false,
    lastSeededPageSignature: undefined,
  };
}

export function mergeUsersByUuid(
  existingUsers: UserWithMembership[],
  incomingUsers: UserWithMembership[],
): UserWithMembership[] {
  if (incomingUsers.length === 0) {
    return existingUsers;
  }

  const usersByUuid = new Map(existingUsers.map((user) => [user.uuid, user]));

  for (const user of incomingUsers) {
    usersByUuid.set(user.uuid, user);
  }

  return Array.from(usersByUuid.values());
}

export function buildPageSignature(currentPageData: UserWithMembership[] | undefined): string | undefined {
  return currentPageData
    ?.map((user) => `${user.uuid}:${user.hasMembership ? 1 : 0}`)
    .sort()
    .join("|");
}

export function syncGroupMembershipSelectionState(
  previousState: GroupMembershipSelectionState,
  currentPageData: UserWithMembership[] | undefined,
): GroupMembershipSelectionState {
  const currentPageSignature = buildPageSignature(currentPageData);

  if (currentPageSignature === previousState.lastSeededPageSignature) {
    return previousState;
  }

  const currentPageInitialUsers = currentPageData?.filter((user) => user.hasMembership) ?? [];
  const nextState: GroupMembershipSelectionState = {
    ...previousState,
    lastSeededPageSignature: currentPageSignature,
  };

  if (currentPageInitialUsers.length === 0) {
    return nextState;
  }

  const discoveredInitialUsers = mergeUsersByUuid(previousState.discoveredInitialUsers, currentPageInitialUsers);

  if (!previousState.isInitialAutoSelectionSuspended) {
    return {
      ...nextState,
      discoveredInitialUsers,
    };
  }

  const nextRemovedUuids = new Set(previousState.removedInitialUserUuids);

  for (const user of currentPageInitialUsers) {
    nextRemovedUuids.add(user.uuid);
  }

  return {
    ...nextState,
    discoveredInitialUsers,
    removedInitialUserUuids: Array.from(nextRemovedUuids),
  };
}
